import React, { useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import Topbar from '../../../components/layout/Topbar';
import { Icon, IconButton } from '../../../components/ui';
import { SettingsContext } from '../../../contexts';
import { PxCalculation, CalcSummary } from '../molecules';
import { useRequestCalculationContext } from '../../hooks/useRequestCalculation';
import { useRequestContext } from '../../hooks/useRequest';
import '../../px-live-calculation.scss';

const BODY_LOCK_CLASS = 'px-live-calculation-open';

function CloseButton({ onClick }) {
  return (
    <IconButton
      size="lg"
      round
      label="Close live calculation"
      className="px-live-calculation-close"
      data-btn="px-calc-close"
      onClick={onClick}
    >
      <Icon name="X" size="sm" />
    </IconButton>
  );
}

function CalculationBody({
  calcSummaryProps,
  pxCalculationProps,
}) {
  return (
    <div className="px-calculation-container">
      <div className="px-calc-banner-shell">
        <div className="px-calc-content">
          <PxCalculation {...pxCalculationProps} />
        </div>
      </div>

      <div className="px-calc-content">
        <h2 className="px-calc-page-title">Part Exchange Summary</h2>
        <CalcSummary {...calcSummaryProps} />
      </div>
    </div>
  );
}

export function RequestCalculator({
  isOrderFlow,
  openQuote,
  closeQuote,
  orderFlowQuoteId,
  orderFlowItemId,
  open,
  onClose,
  onCloseBtnClick,
  topTitle,
  netPay,
  profitValue,
  profitErr,
  // Currency netPay is denominated in — forwarded to PxCalculation's
  // Left-to-pay label (falls back to store base there).
  dealCurrency,
  requestType,
  order,
  product,
  request,
  // Multi-main: forwarded from PriceCalculator to CalcSummary so the
  // popup iterates every main slot's listing/request, not just the
  // first.
  mainEntries,
  pxRequests,
  pxProducts,
  client,
  getSelectionForItem,
}) {
  const handleClose = onCloseBtnClick || onClose;

  useEffect(() => {
    if (!open || !isOrderFlow) return undefined;

    document.body.classList.add(BODY_LOCK_CLASS);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.classList.remove(BODY_LOCK_CLASS);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isOrderFlow]);

  const calcSummaryProps = {
    isOrderFlow,
    openQuote,
    closeQuote,
    orderFlowQuoteId,
    orderFlowItemId,
    requestType,
    topTitle,
    order,
    product,
    request,
    mainEntries,
    pxRequests,
    pxProducts,
    client,
    getSelectionForItem,
  };

  const pxCalculationProps = {
    request,
    netPay,
    profitValue,
    profitErr,
    dealCurrency,
  };

  if (!open) {
    return null;
  }

  const pageClassName = [
    'px-live-calculation-page',
    isOrderFlow ? 'px-live-calculation-page--overlay' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={pageClassName}
      role={isOrderFlow ? 'dialog' : undefined}
      aria-modal={isOrderFlow || undefined}
    >
      {!isOrderFlow && (
        <div className="px-live-calculation-mobile-topbar">
          <Topbar
            title="Part Exchange"
            description="Live Calculation"
            showBackButton
            onBack={handleClose}
          />
        </div>
      )}
      <CloseButton onClick={handleClose} />
      <CalculationBody
        calcSummaryProps={calcSummaryProps}
        pxCalculationProps={pxCalculationProps}
      />
    </div>
  );
}

export function RequestCalculationTemplate() {
  const settings = useContext(SettingsContext);
  const history = useHistory();
  const { requestType, requestId } = useParams();
  const { order, product, request, pxRequests, pxProducts, client } = useRequestContext();
  const {
    getSelectionForItem,
    results: { netPay, profit: [profitValue, profitErr] = [] },
    dealCurrency,
  } = useRequestCalculationContext();

  const onClose = () => {
    history.push(`/request/${requestType}/${requestId}?${settings.iframeQueryParams}`);
  }

  return (
    <RequestCalculator
      open
      onClose={onClose}
      topTitle={requestType === 'px-cart' ? 'Cart Order' : 'Listing'}
      netPay={netPay}
      profitValue={profitValue}
      profitErr={profitErr}
      dealCurrency={dealCurrency}
      requestType={requestType}
      order={order}
      product={product}
      request={request}
      pxRequests={pxRequests}
      pxProducts={pxProducts}
      client={client}
      getSelectionForItem={getSelectionForItem}
      onCloseBtnClick={onClose}
    />
  );
}
